
import { useState, FormEvent } from 'react';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    if (formData.message.length > 500) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('service', formData.service);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6dl4bqtehdqnvnpj0a0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            Skontaktuj Się z Nami
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Masz pytania lub potrzebujesz pomocy? Wypełnij formularz, a my skontaktujemy się z Tobą w ciągu 24 godzin
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          <form
            id="contact-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="bg-gray-50 rounded-3xl p-8 lg:p-12 shadow-lg"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <div className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                    Imię i Nazwisko *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                    placeholder="Jan Kowalski"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                    placeholder="jan.kowalski@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                    Telefon *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                    placeholder="+48 691 284 309"
                  />
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <label htmlFor="service" className="block text-sm font-semibold text-gray-700 mb-2">
                    Rodzaj Usługi *
                  </label>
                  <select
                    id="service"
                    name="service"
                    required
                    value={formData.service}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all cursor-pointer"
                  >
                    <option value="">Wybierz usługę</option>
                    <option value="osuszanie">Osuszanie Pomieszczeń</option>
                    <option value="wynajem">Wynajem Osuszaczy</option>
                    <option value="lokalizacja">Lokalizacja Wycieków</option>
                    <option value="remont">Remonty Kompleksowe</option>
                    <option value="wykończenia">Wykończenia Wnętrz</option>
                    <option value="awaria">Interwencja Awaryjna</option>
                  </select>
                </div>

                <div className="h-full">
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                    Wiadomość *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={6}
                    maxLength={500}
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all resize-none"
                    placeholder="Opisz swoją sytuację..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.message.length}/500 znaków
                  </p>
                </div>
              </div>
            </div>

            <div className="text-center mt-8">
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-12 py-4 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-all inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'Wysyłanie...' : 'Wyślij Zapytanie'}
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>

            {submitStatus === 'success' && (
              <div className="mt-6 p-4 bg-green-100 text-green-700 rounded-lg text-center">
                Dziękujemy! Twoja wiadomość została wysłana. Skontaktujemy się z Tobą wkrótce.
              </div>
            )}
            {submitStatus === 'error' && (
              <div className="mt-6 p-4 bg-red-100 text-red-700 rounded-lg text-center">
                Wystąpił błąd. Spróbuj ponownie lub skontaktuj się telefonicznie.
              </div>
            )}
          </form>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <i className="ri-phone-line text-4xl text-blue-600"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Telefon</h3>
              <a href="tel:+48691284309" className="text-gray-600 hover:text-blue-600 transition-colors cursor-pointer">
                +48 691 284 309
              </a>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <i className="ri-mail-line text-4xl text-blue-600"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Email</h3>
              <a href="mailto:biuro@tmgarant.pl" className="text-gray-600 hover:text-blue-600 transition-colors cursor-pointer">
                biuro@tmgarant.pl
              </a>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <i className="ri-map-pin-line text-4xl text-blue-600"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Adres</h3>
              <p className="text-gray-600">
                ul. Anny Jagiellonki 6A/4<br />
                65-115 Zielona Góra
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
